import {KpisList} from '@domain/kpis'
import {type Iso8601, type TimeZone} from '@hconnect/common/types'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useMemo} from 'react'

import {type KpiQuarterlyData} from '../types'

import {
  useAlternativeFuelRatesQuarterly,
  useCementProductionQuarterly,
  useClinkerProductionQuarterly,
  useKilnHeatConsumptionQuarterly,
  useTechnicalCementPowerConsumptionsQuarterly
} from './api'

const TAB_QUARTERLY_API_MAPPING = {
  [KpisList.CementProduction]: useCementProductionQuarterly,
  [KpisList.ClinkerProduction]: useClinkerProductionQuarterly,
  [KpisList.KilnHeatConsumption]: useKilnHeatConsumptionQuarterly,
  [KpisList.AlternativeFuelRate]: useAlternativeFuelRatesQuarterly,
  [KpisList.TechnicalCementPowerCons]: useTechnicalCementPowerConsumptionsQuarterly
}

type ReturnType = {
  data: KpiQuarterlyData | null
  isInitialLoading: boolean
  error: AxiosError<unknown> | null
  refetch: () => Promise<UseQueryResult<KpiQuarterlyData | null, AxiosError> | null>
}

type UseKpiQuarterlyValueParams = {
  timestamp: Iso8601
  currentTab: KpisList
  timezone: TimeZone
  locationId?: string
}

export const useKpiQuarterlyValue = ({
  timestamp,
  currentTab,
  timezone,
  locationId
}: UseKpiQuarterlyValueParams): ReturnType => {
  const useQuarterlyApiHook = useMemo(
    () => TAB_QUARTERLY_API_MAPPING[currentTab] || useCementProductionQuarterly,
    [currentTab]
  )

  const {data, isInitialLoading, error, refetch} = useQuarterlyApiHook(
    timestamp,
    timezone,
    locationId
  )

  return useMemo(
    () => ({data, isInitialLoading, error, refetch}),
    [data, isInitialLoading, error, refetch]
  )
}
